import {useAppSelector} from '../../../store/store.ts';
import {Status} from '../../../enums/status.ts';
import {CenteredSpinner} from '../../../components/spinner/centered_spinner.tsx';
import {__} from '../../../support/lang/translations.ts';
import buttonStyles from '../../../components/button/button.module.scss';
import formStyles from '../../../components/form/form.module.scss';
import {Button} from '../../../components/button/button.tsx';
import {ButtonColorType} from '../../../enums/button_color_type.ts';
import {Vehicle} from '../../../gql/customer/graphql.ts';
import {VehicleLogo, VehicleLogoSize} from '../../../components/vehicle/vehicle_logo.tsx';
import {VehicleBrandModel} from '../../../components/vehicle/vehicle_brand_model.tsx';
import {Identifier} from '../../../components/identifier/identifier.tsx';
import {FlexRow} from '../../../components/utility/flex_row.tsx';
import {FlexColumn} from '../../../components/utility/flex_column.tsx';
import {Alignment} from '../../../enums/alignment.ts';
import {Spacer} from '../../../components/utility/spacer.tsx';
import {brandLogo} from '../../../support/brand_logo.ts';

interface TitleProps {
    brand?: string | null;
}
export function VehicleSelectTitle(props: TitleProps) {
    return (
        <>
            <h1>{__('appointment.vehicle_select.title')}</h1>
            {props.brand != null && <h2>{__('appointment.vehicle_select.subtitle', {brand: props.brand})}</h2>}
        </>
    );
}

interface ContentProps {
    isEditing: boolean;
    vehicles: Vehicle[];
    vehicleId: number | null;
}

export function VehicleSelectContent(props: ContentProps) {
    const customerInfo = useAppSelector((state) => state.customerInfo);
    if (customerInfo.status !== Status.FULFILLED) {
        return <CenteredSpinner />;
    }

    if (!props.vehicles || props.vehicles.length === 0) {
        return (
            <FlexRow alignment={Alignment.Center}>
                <FlexColumn alignment={Alignment.Center}>
                    <div>
                        <h3>{__('appointment.vehicle_select.empty.title')}</h3>
                        <p style={{maxWidth: 450}}>{__('appointment.vehicle_select.empty.message')}</p>
                    </div>
                    <Spacer />
                    <div>
                        <Button to="/vehicles">
                            <i className="fa-light fa-car" />
                            {__('appointment.vehicle_select.empty.action')}
                        </Button>
                    </div>
                </FlexColumn>
            </FlexRow>
        );
    }
    const selectedVehicle = props.vehicles.find((vehicle) => vehicle?.id === props.vehicleId);
    if (!props.vehicleId || !selectedVehicle) {
        return (
            <FlexRow alignment={Alignment.Center}>
                <FlexColumn alignment={Alignment.Center}>
                    <div>
                        <h3>{__('appointment.vehicle_select.no_vehicle.title')}</h3>
                        <p style={{maxWidth: 450}}>{__('appointment.vehicle_select.no_vehicle.message')}</p>
                    </div>
                </FlexColumn>
            </FlexRow>
        );
    }

    return (
        <div className={formStyles.form}>
            <VehicleLogo
                alt={selectedVehicle.brand ?? 'auto'}
                src={brandLogo(selectedVehicle.brand)}
                size={VehicleLogoSize.Large}
            />
            <VehicleBrandModel brand={selectedVehicle.brand ?? ''} model={selectedVehicle.model ?? ''}>
                <Identifier
                    identifier={selectedVehicle.identifiers?.find((id) => id?.type === 'license_plate')?.value ?? ''}
                />
            </VehicleBrandModel>
        </div>
    );
}

interface FooterProps {
    isEditing: boolean;
    isDisabled: boolean;
    setIsEditing: (isEditing: boolean) => void;
    onNextSlide: () => void;
    vehicles: Vehicle[] | null;
}

export function VehicleSelectFooter(props: FooterProps) {
    return (
        <>
            {props.vehicles != null && props.vehicles.length > 1 ? (
                <Button
                    disabled={props.isDisabled}
                    className={buttonStyles.button_step}
                    type="button"
                    onClick={() => props.setIsEditing(true)}
                    colorType={ButtonColorType.Black}
                >
                    <i className="fa-light fa-pen" />
                    <span>{__('appointment.vehicle_select.footer.buttons.select')}</span>
                </Button>
            ) : (
                <span />
            )}

            <Button
                disabled={props.isDisabled}
                className={buttonStyles.button_step}
                type="button"
                onClick={() => props.onNextSlide()}
                colorType={ButtonColorType.Green}
            >
                <i className="fa-light fa-check" />
                <span>{__('appointment.vehicle_select.footer.buttons.next')}</span>
            </Button>
        </>
    );
}
